/**
 * Monaco Editor Language Service Provider
 */
import type { editor, IDisposable, languages, Position } from 'monaco-editor';

import { LIBRARY_IMPORT_STATEMENTS } from '@/shared/constants/libraryConfig';

import type { ModuleTarget } from '@/entities/langserver/api/types';

import {
  type AutoImport,
  buildTemplateSections,
  type CompletionItem,
  convertToCursorPosition,
  type Diagnostic,
  fetchCompletions,
  fetchDiagnostics,
  fetchHover,
  fetchSignature,
  type HoverInfo,
  type SignatureInfo,
} from '@/features/code-analysis';

type Monaco = typeof import('monaco-editor');

export class PythonLanguageService {
  private monaco: Monaco;
  private clientId: string;
  private target: ModuleTarget;
  private getSignature: () => string;
  private getBody: () => string;
  private getAutoImports: () => AutoImport[];
  private disposables: IDisposable[] = [];

  constructor(
    monaco: Monaco,
    clientId: string,
    target: ModuleTarget = 'run',
    getSignature: () => string,
    getBody: () => string,
    getAutoImports: () => AutoImport[]
  ) {
    this.monaco = monaco;
    this.clientId = clientId;
    this.target = target;
    this.getSignature = getSignature;
    this.getBody = getBody;
    this.getAutoImports = getAutoImports;
  }

  private buildImportStatements(): string {
    const autoImports = this.getAutoImports();
    const importLines = autoImports
      .map((imp) => LIBRARY_IMPORT_STATEMENTS[imp.module] || `import ${imp.module}`)
      .join('\n');

    return importLines ? `${importLines}\n\n` : '';
  }

  private buildAnalysisRequest(position: Position) {
    const signature = this.getSignature();
    const mutableCode = this.getBody();

    const template = buildTemplateSections(signature, mutableCode, '');

    const signatureLineCount = signature ? (signature.match(/\n/g) || []).length : 0;

    const cursor = convertToCursorPosition(position.lineNumber, position.column, signatureLineCount, !!signature);

    return {
      template,
      cursor,
      autoImports: this.getAutoImports(),
      clientId: this.clientId,
    };
  }

  private buildCompletionRequest(position: Position) {
    const signature = this.getSignature();
    const mutableCode = this.getBody();
    const imports = this.buildImportStatements();
    const fullCode = `${imports}${signature}\n${mutableCode}`;

    const template = buildTemplateSections('', fullCode, '');

    const signatureLineCount = signature ? (signature.match(/\n/g) || []).length : 0;
    const importsLineCount = imports ? (imports.match(/\n/g) || []).length : 0;

    const cursor = convertToCursorPosition(
      position.lineNumber,
      position.column,
      signatureLineCount + importsLineCount,
      !!(imports || signature)
    );

    return {
      template,
      cursor,
      autoImports: this.getAutoImports(),
      clientId: this.clientId,
    };
  }

  registerCompletionProvider(): void {
    const provider = this.monaco.languages.registerCompletionItemProvider('python', {
      triggerCharacters: ['.'],
      provideCompletionItems: async (
        model: editor.ITextModel,
        position: Position
      ): Promise<languages.CompletionList> => {
        try {
          const request = this.buildCompletionRequest(position);
          const items = await fetchCompletions(this.target, request);

          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          const suggestions = items.map((item: CompletionItem) => ({
            label: item.name,
            kind: this.getCompletionKind(item.type),
            documentation: item.description || '',
            insertText: item.name,
            range: range,
            sortText: item.module_name ? `1_${item.name}` : `0_${item.name}`,
          }));

          return { suggestions };
        } catch (error) {
          console.error('Completion provider error:', error);
          return { suggestions: [] };
        }
      },
    });

    this.disposables.push(provider);
  }

  registerHoverProvider(): void {
    const provider = this.monaco.languages.registerHoverProvider('python', {
      provideHover: async (_model: editor.ITextModel, position: Position): Promise<languages.Hover | null> => {
        try {
          const request = this.buildAnalysisRequest(position);
          const hoverInfo = await fetchHover(this.target, request);

          if (!hoverInfo) return null;

          const markdown = this.buildHoverMarkdown(hoverInfo);

          return {
            contents: [{ value: markdown }],
          };
        } catch (error) {
          console.error('Hover provider error:', error);
          return null;
        }
      },
    });

    this.disposables.push(provider);
  }

  registerSignatureProvider(): void {
    const provider = this.monaco.languages.registerSignatureHelpProvider('python', {
      signatureHelpTriggerCharacters: ['(', ','],
      provideSignatureHelp: async (
        _model: editor.ITextModel,
        position: Position
      ): Promise<languages.SignatureHelpResult | null> => {
        try {
          const request = this.buildAnalysisRequest(position);
          const signatures = await fetchSignature(this.target, request);

          if (signatures.length === 0) return null;

          const result: languages.SignatureHelp = {
            signatures: signatures.map((sig: SignatureInfo) => ({
              label: sig.label,
              parameters: sig.params.map((param) => ({
                label: param.name,
                documentation: param.description,
              })),
            })),
            activeSignature: 0,
            activeParameter: signatures[0]?.index || 0,
          };

          return {
            value: result,
            dispose: () => {},
          };
        } catch (error) {
          console.error('Signature provider error:', error);
          return null;
        }
      },
    });

    this.disposables.push(provider);
  }

  async updateDiagnostics(model: editor.ITextModel): Promise<void> {
    try {
      const lastPosition = model.getPositionAt(model.getValue().length);
      const request = this.buildAnalysisRequest(lastPosition);
      const diagnostics = await fetchDiagnostics(this.target, request);

      const markers = diagnostics.map((diag: Diagnostic) => ({
        severity: this.monaco.MarkerSeverity.Error,
        startLineNumber: diag.line,
        startColumn: diag.column + 1,
        endLineNumber: diag.end_line,
        endColumn: (diag.end_column ?? diag.column) + 1,
        message: diag.message,
      }));

      this.monaco.editor.setModelMarkers(model, 'python-diagnostics', markers);
    } catch (error) {
      console.error('Diagnostics error:', error);
    }
  }

  dispose(): void {
    this.disposables.forEach((d) => d.dispose());
    this.disposables = [];
  }

  private getCompletionKind(type: string): languages.CompletionItemKind {
    switch (type) {
      case 'function':
        return this.monaco.languages.CompletionItemKind.Function;
      case 'class':
        return this.monaco.languages.CompletionItemKind.Class;
      case 'module':
        return this.monaco.languages.CompletionItemKind.Module;
      case 'variable':
      default:
        return this.monaco.languages.CompletionItemKind.Variable;
    }
  }

  private buildHoverMarkdown(info: HoverInfo): string {
    if (!info) return '';

    const parts: string[] = [];

    parts.push(`**${info.name}**`);

    if (info.type) {
      parts.push(`\nType: \`${info.type}\``);
    }

    if (info.module_name) {
      parts.push(`\nModule: \`${info.module_name}\``);
    }

    if (info.description) {
      parts.push(`\n\n${info.description}`);
    }

    if (info.docstring) {
      parts.push(`\n\n---\n\n${info.docstring}`);
    }

    if (info.signatures && info.signatures.length > 0) {
      parts.push(`\n\n**Signatures:**\n${info.signatures.map((s) => `\n- \`${s}\``).join('')}`);
    }

    return parts.join('');
  }
}
